<template>
  <transition name="initial-loading-fade">
    <div v-if="active" class="initial-loading" :style="backgroundStyle" role="status" aria-label="Carregando cardápio" aria-live="polite">
      <div class="initial-loading__fill" aria-hidden="true"></div>
      <div class="initial-loading__indicator" aria-hidden="true">
        <span class="initial-loading__dots"><i></i><i></i><i></i><i></i></span>
      </div>
    </div>
  </transition>
</template>

<script>
// Conta o tempo enquanto a requisição acontece, sem adicionar espera ao final.
const MINIMUM_DURATION = 600;

export const initialLoadingMixin = {
  data() {
    return { initialLoadingElapsed: false, initialLoadingTimer: null };
  },
  computed: {
    showInitialLoading() {
      return this.load || !this.initialLoadingElapsed;
    }
  },
  mounted() {
    this.initialLoadingTimer = setTimeout(() => {
      this.initialLoadingElapsed = true;
      this.initialLoadingTimer = null;
    }, MINIMUM_DURATION);
  },
  beforeDestroy() {
    clearTimeout(this.initialLoadingTimer);
  }
};

export default {
  name: 'InitialLoading',
  props: { active: { type: Boolean, default: true } },
  data() {
    return { cachedColor: '', configuredColor: '' };
  },
  computed: {
    cacheKey() {
      return `lecard:initial-theme:v2:${window.location.host}:${this.$route?.params?.id || ''}`;
    },
    backgroundStyle() {
      return { '--initial-loading-color': this.configuredColor || this.cachedColor || '#FC2573' };
    }
  },
  created() {
    this.readConfiguredColor();
    try {
      const color = localStorage.getItem(this.cacheKey);
      if (color && CSS.supports('color', color)) this.cachedColor = color;
    } catch (error) {
      // O carregamento também funciona quando o armazenamento está indisponível.
    }
  },
  watch: {
    active(value) {
      if (!value) {
        this.readConfiguredColor();
        this.saveTheme();
      }
    }
  },
  methods: {
    readConfiguredColor() {
      // A variável computada inclui o cinza padrão do CSS, que não é o tema da loja.
      const color = document.documentElement.style.getPropertyValue('--lecard-primary').trim();
      if (color && CSS.supports('color', color)) this.configuredColor = color;
    },
    saveTheme() {
      try {
        const color = document.documentElement.style.getPropertyValue('--lecard-primary').trim();
        if (color && CSS.supports('color', color)) localStorage.setItem(this.cacheKey, color);
      } catch (error) {
        // Guardar o tema é opcional.
      }
    }
  },
  beforeDestroy() {
    this.saveTheme();
  }
};
</script>

<style scoped>
.initial-loading {
  --initial-loading-color: #FC2573;
  position: fixed;
  inset: 0;
  z-index: 2100;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  isolation: isolate;
}

.initial-loading__fill {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 150vmax;
  height: 150vmax;
  border-radius: 50%;
  background: var(--initial-loading-color);
  transform: translate(-50%, -50%);
  animation: initial-loading-expand 560ms cubic-bezier(0.4, 0, 0.2, 1) both;
}

.initial-loading__indicator {
  position: relative;
  width: 80px;
  height: 11px;
  animation: initial-loading-reveal 160ms 440ms both;
}

.initial-loading__dots {
  position: absolute;
  inset: 0;
}

.initial-loading__dots i {
  position: absolute;
  top: 0;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #fff;
  animation-timing-function: cubic-bezier(0, 1, 1, 0);
}
.initial-loading__dots i:nth-child(1) {
  left: 8px;
  animation: initial-loading-dot-grow 600ms infinite;
}
.initial-loading__dots i:nth-child(2) {
  left: 8px;
  animation: initial-loading-dot-move 600ms infinite;
}
.initial-loading__dots i:nth-child(3) {
  left: 32px;
  animation: initial-loading-dot-move 600ms infinite;
}
.initial-loading__dots i:nth-child(4) {
  left: 56px;
  animation: initial-loading-dot-shrink 600ms infinite;
}

.initial-loading-fade-leave-active { transition: opacity 180ms ease-out; }
.initial-loading-fade-leave-to { opacity: 0; }

@keyframes initial-loading-expand {
  from { transform: translate(-50%, -50%) scale(0); }
  to { transform: translate(-50%, -50%) scale(1); }
}
@keyframes initial-loading-reveal {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes initial-loading-dot-grow {
  from { transform: scale(0); }
  to { transform: scale(1); }
}
@keyframes initial-loading-dot-move {
  from { transform: translateX(0); }
  to { transform: translateX(24px); }
}
@keyframes initial-loading-dot-shrink {
  from { transform: scale(1); }
  to { transform: scale(0); }
}
@media (prefers-reduced-motion: reduce) {
  .initial-loading__fill,
  .initial-loading__indicator,
  .initial-loading__dots i { animation: none; }
  .initial-loading-fade-leave-active { transition: none; }
}
</style>
